<template>
  <Teleport :to="teleportTarget">
    <!--
      focusin.stop: у q-dialog на document висит перехват focusin (focus trap).
      Без остановки всплытия фокус с поля поиска в телепортированном попапе уводит
      обратно внутрь диалога — курсор в инпуте не держится.
    -->
    <div
      v-if="visible"
      ref="floatingEl"
      class="selector-popup"
      :class="{
        'selector-popup--positioned': isPositioned,
        'selector-popup--resizing': isResizing,
      }"
      :style="popupStyles"
      @focusin.stop
    >
      <div
        v-for="corner in freeCorners"
        :key="corner"
        :class="[
          'selector-popup__resize-handle',
          `selector-popup__resize-handle--${corner}`,
        ]"
        @mousedown.prevent="startResize(corner, $event)"
      >
        <svg viewBox="0 0 10 10" class="selector-popup__resize-icon">
          <path
            d="M3 3L7 7M5 7H7V5"
            stroke="currentColor"
            stroke-width="1.5"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <div class="selector-popup__content" ref="contentEl">
        <slot :content-height="contentHeight" />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, toRef, nextTick, type ComponentPublicInstance } from 'vue';
import { useFloating, size, offset, autoUpdate, type Placement } from '@floating-ui/vue';
import {
  registerPopup,
  unregisterPopup,
  currentPopupId,
  isCurrentPopup,
} from './selectorPopupRegistry';

type TriggerRef = HTMLElement | ComponentPublicInstance | null | undefined;
type TeleportTarget = string | HTMLElement;

const props = withDefaults(
  defineProps<{
    visible: boolean;
    triggerEl?: TriggerRef;
    teleportTo?: TeleportTarget;
    /** Стартовая ширина при обычном контенте (одна колонка и т.п.). */
    defaultWidth?: number;
    /**
     * Если true — при открытии берётся wideDefaultWidth (селекторы full / deps, две колонки).
     * Иначе — defaultWidth.
     */
    wideLayout?: boolean;
    /** Стартовая ширина при wideLayout (по умолчанию шире, чем defaultWidth). */
    wideDefaultWidth?: number;
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
  }>(),
  {
    triggerEl: undefined,
    teleportTo: 'body',
    defaultWidth: 300,
    wideLayout: false,
    wideDefaultWidth: 560,
    minWidth: 200,
    maxWidth: 2000,
    minHeight: 150,
    maxHeight: 500,
  },
);

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void;
}>();

const myId = Symbol('selector-popup');
const teleportTarget = computed<TeleportTarget>(() => props.teleportTo || 'body');

const referenceEl = computed((): HTMLElement | null => {
  const el = props.triggerEl;
  if (!el) return null;
  if (el instanceof HTMLElement) return el;
  if (typeof el === 'object' && '$el' in el)
    return (el as ComponentPublicInstance).$el as HTMLElement;
  return null;
});

const floatingEl = ref<HTMLElement | null>(null);
const contentEl = ref<HTMLElement | null>(null);
const contentMinWidth = ref(0);

const userWidth = ref(props.defaultWidth);
const userHeight = ref<number | null>(null);
const availableMaxHeight = ref(600);
const lockedPlacement = ref<Placement>('bottom-start');

function updateContentMinSize() {
  // Не замеряем scrollWidth — при min-width:100% на контенте он равен
  // ширине попапа и блокирует сужение. Используем props.minWidth как пол.
  contentMinWidth.value = 0;
}

function computeBestPlacement(refEl: HTMLElement): Placement {
  const rect = refEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spaceAbove = rect.top;
  const spaceBelow = vh - rect.bottom;
  const spaceLeft = rect.left;
  const spaceRight = vw - rect.right;

  const vertical = spaceBelow >= spaceAbove ? 'bottom' : 'top';
  const horizontal = spaceRight >= spaceLeft ? 'right' : 'left';

  const maxVertical = Math.max(spaceAbove, spaceBelow);
  const maxHorizontal = Math.max(spaceLeft, spaceRight);

  if (maxVertical >= maxHorizontal) {
    const align = spaceRight >= spaceLeft ? 'start' : 'end';
    return `${vertical}-${align}` as Placement;
  }
  const align = spaceBelow >= spaceAbove ? 'start' : 'end';
  return `${horizontal}-${align}` as Placement;
}

const openRef = toRef(props, 'visible');

const { floatingStyles, placement, isPositioned } = useFloating(
  referenceEl,
  floatingEl,
  {
    strategy: 'fixed',
    placement: lockedPlacement,
    open: openRef,
    middleware: [
      offset(4),
      size({
        apply({ availableHeight }) {
          availableMaxHeight.value = Math.max(
            props.minHeight,
            availableHeight - 16,
          );
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  },
);

const effectiveMinWidth = computed(() =>
  Math.max(props.minWidth, Math.min(contentMinWidth.value || 0, props.maxWidth)),
);

const effectiveHeight = computed(() => {
  if (userHeight.value != null) return userHeight.value;
  return Math.min(availableMaxHeight.value, props.maxHeight);
});

const contentHeight = computed(() => {
  return Math.max(0, effectiveHeight.value - 2);
});

const popupStyles = computed((): Record<string, string> => ({
  ...(floatingStyles.value as unknown as Record<string, string>),
  width: `${userWidth.value}px`,
  height: `${effectiveHeight.value}px`,
  minWidth: `${effectiveMinWidth.value}px`,
  minHeight: `${props.minHeight}px`,
}));

function getAnchorCorner(p: string): string {
  const parts = (p || 'bottom-start').split('-');
  const side = parts[0];
  const align = parts[1] || 'start';

  if (side === 'right') return align === 'start' ? 'tl' : 'bl';
  if (side === 'left') return align === 'start' ? 'tr' : 'br';
  if (side === 'bottom') return align === 'start' ? 'tl' : 'tr';
  if (side === 'top') return align === 'start' ? 'bl' : 'br';
  return 'tl';
}

const allCorners = ['tl', 'tr', 'bl', 'br'] as const;

const freeCorners = computed(() => {
  const anchor = getAnchorCorner(placement.value);
  return allCorners.filter((c) => c !== anchor);
});

// --- Resize ---
const isResizing = ref(false);
let resizeStartX = 0;
let resizeStartY = 0;
let resizeStartWidth = 0;
let resizeStartHeight = 0;
let resizeCorner = '';

function startResize(corner: string, e: MouseEvent) {
  isResizing.value = true;
  resizeCorner = corner;
  resizeStartX = e.clientX;
  resizeStartY = e.clientY;
  resizeStartWidth = userWidth.value;
  resizeStartHeight =
    userHeight.value ?? floatingEl.value?.offsetHeight ?? 300;
  document.addEventListener('mousemove', onResizeMove);
  document.addEventListener('mouseup', onResizeEnd);
}

function onResizeMove(e: MouseEvent) {
  const dx = e.clientX - resizeStartX;
  const dy = e.clientY - resizeStartY;
  const xMult = resizeCorner.includes('r') ? 1 : -1;
  const yMult = resizeCorner.includes('b') ? 1 : -1;
  userWidth.value = Math.max(
    effectiveMinWidth.value,
    Math.min(props.maxWidth, resizeStartWidth + dx * xMult),
  );
  userHeight.value = Math.max(
    props.minHeight,
    Math.min(
      availableMaxHeight.value,
      resizeStartHeight + dy * yMult,
    ),
  );
}

function onResizeEnd() {
  isResizing.value = false;
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);
}

// --- Close ---
function close() {
  emit('update:visible', false);
}

function onDocumentMousedown(e: MouseEvent) {
  if (!props.visible) return;
  const target = e.target as Node;
  if (floatingEl.value?.contains(target)) return;
  const ref = referenceEl.value;
  if (ref?.contains(target)) return;

  e.stopImmediatePropagation();
  e.stopPropagation();

  // Закрытие «снаружи»: mousedown глушим, но q-dialog часто закрывается по click на backdrop.
  // Одноразовый capture click — первый клик закрывает только селектор, второй — уже диалог.
  const stopNextClick = (ce: Event) => {
    ce.stopImmediatePropagation();
    ce.stopPropagation();
    document.removeEventListener('click', stopNextClick, true);
  };
  document.addEventListener('click', stopNextClick, true);
  setTimeout(() => document.removeEventListener('click', stopNextClick, true), 500);

  close();
}

function onDocumentKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.visible) {
    e.stopPropagation();
    close();
  }
}

// --- Singleton ---
watch(currentPopupId, () => {
  if (props.visible && !isCurrentPopup(myId)) {
    close();
  }
});

// --- Visibility lifecycle ---
watch(
  () => props.visible,
  (val) => {
    if (val) {
      registerPopup(myId);
      userWidth.value = props.wideLayout ? props.wideDefaultWidth : props.defaultWidth;
      userHeight.value = null;
      nextTick(() => {
        updateContentMinSize();
      });
      const refEl = referenceEl.value;
      if (refEl) {
        lockedPlacement.value = computeBestPlacement(refEl);
      }
      document.addEventListener('mousedown', onDocumentMousedown, true);
      document.addEventListener('keydown', onDocumentKeydown, true);
    } else {
      unregisterPopup(myId);
      document.removeEventListener('mousedown', onDocumentMousedown, true);
      document.removeEventListener('keydown', onDocumentKeydown, true);
    }
  },
);

onBeforeUnmount(() => {
  unregisterPopup(myId);
  document.removeEventListener('mousedown', onDocumentMousedown, true);
  document.removeEventListener('keydown', onDocumentKeydown, true);
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);
});
</script>

<style scoped lang="scss">
.selector-popup {
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: var(--Background-White, #FFF);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.20);
  overflow: hidden;
  visibility: hidden;

  &--positioned {
    visibility: visible;
  }

  &--resizing {
    user-select: none;
  }

  &__content {
    flex: 1;
    min-height: 0;
    /* В колонке flex у item по умолчанию min-width:auto — контент не даёт сузить попап. */
    min-width: 0;
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding-bottom: 8px;

    :deep(*) {
      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: #fff;
      }

      &::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background: var(--color-border, #D2D2D2);
      }

      scrollbar-width: thin;
      scrollbar-color: var(--color-border, #D2D2D2) #fff;
    }
  }

  &__resize-handle {
    position: absolute;
    width: 18px;
    height: 18px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s;
    color: #979797;

    &:hover {
      opacity: 0.7;
    }

    &--tl {
      top: 0;
      left: 0;
      cursor: nwse-resize;
    }
    &--tr {
      top: 0;
      right: 0;
      cursor: nesw-resize;
    }
    &--bl {
      bottom: 0;
      left: 0;
      cursor: nesw-resize;
    }
    &--br {
      bottom: 0;
      right: 0;
      cursor: nwse-resize;
    }
  }

  &__resize-icon {
    width: 10px;
    height: 10px;

    .selector-popup__resize-handle--tl & {
      transform: rotate(180deg);
    }
    .selector-popup__resize-handle--tr & {
      transform: rotate(270deg);
    }
    .selector-popup__resize-handle--bl & {
      transform: rotate(90deg);
    }
  }
}
</style>
